import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiMenu, FiX } from 'react-icons/fi'
import Sidebar from '../../components/admin/Sidebar'
import DataTable from '../../components/admin/DataTable'
import AdminModal from '../../components/admin/AdminModal'
import {
  fetchAdminStats,
  fetchAdminVolunteers,
  fetchAdminCleaningSlots,
  fetchAdminMissions,
  fetchAdminImpactStats,
  fetchAdminHeritagePlaces,
  fetchAdminGalleryItems,
  fetchAdminNewsletters,
  fetchAdminLeaderboard,
  createAdminRecord,
  updateAdminRecord,
  deleteAdminRecord
} from '../../utils/api'

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [tableData, setTableData] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingData, setEditingData] = useState(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const token = localStorage.getItem('admin_token')
    if (!token) {
      navigate('/admin/login')
    } else {
      loadData(activeTab)
    }
  }, [activeTab, navigate])

  const loadData = async (tab) => {
    setLoading(true)
    try {
      if (tab === 'dashboard') {
        const res = await fetchAdminStats()
        setStats(res.data?.data)
      } else if (tab === 'volunteers') {
        const res = await fetchAdminVolunteers()
        setTableData(res.data?.data || [])
      } else if (tab === 'cleaning_slots') {
        const res = await fetchAdminCleaningSlots()
        setTableData(res.data?.data || [])
      } else if (tab === 'missions') {
        const res = await fetchAdminMissions()
        setTableData(res.data?.data || [])
      } else if (tab === 'impact_stats') {
        const res = await fetchAdminImpactStats()
        setTableData(res.data?.data || [])
      } else if (tab === 'heritage_places') {
        const res = await fetchAdminHeritagePlaces()
        setTableData(res.data?.data || [])
      } else if (tab === 'gallery_items') {
        const res = await fetchAdminGalleryItems()
        setTableData(res.data?.data || [])
      } else if (tab === 'newsletters') {
        const res = await fetchAdminNewsletters()
        setTableData(res.data?.data || [])
      } else if (tab === 'leaderboard') {
        const res = await fetchAdminLeaderboard()
        setTableData(res.data?.data || [])
      }
    } catch (error) {
      console.error('Error fetching admin data:', error)
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token')
        navigate('/admin/login')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (formData) => {
    try {
      if (editingData?.id) {
        await updateAdminRecord(activeTab, editingData.id, formData)
      } else {
        await createAdminRecord(activeTab, formData)
      }
      setIsModalOpen(false)
      loadData(activeTab)
    } catch (error) {
      console.error('Error saving record:', error)
      alert('Failed to save record.')
    }
  }

  const handleDelete = async (row) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await deleteAdminRecord(activeTab, row.id)
        loadData(activeTab)
      } catch (error) {
        console.error('Error deleting record:', error)
        alert('Failed to delete record.')
      }
    }
  }

  const openModal = (data = null) => {
    setEditingData(data)
    setIsModalOpen(true)
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="w-10 h-10 border-4 border-accent-maroon border-t-transparent rounded-full animate-spin"></div>
        </div>
      )
    }

    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <StatCard title="Total Volunteers" value={stats?.total_volunteers} />
            <StatCard title="Cleaning Slots" value={stats?.total_cleaning_slots} />
            <StatCard title="Total Missions" value={stats?.total_missions} />
            <StatCard title="Waste Removed (kg)" value={stats?.total_waste_removed} />
          </div>
        )
      case 'volunteers':
        return (
          <DataTable
            title="Volunteers Directory"
            data={tableData}
            onAdd={() => openModal()}
            onEdit={openModal}
            onDelete={handleDelete}
            columns={getColumns('volunteers')}
          />
        )
      case 'cleaning_slots':
        return (
          <DataTable
            title="Cleaning Bookings"
            data={tableData}
            onAdd={() => openModal()}
            onEdit={openModal}
            onDelete={handleDelete}
            columns={getColumns('cleaning_slots')}
          />
        )
      case 'missions':
        return (
          <DataTable
            title="Missions"
            data={tableData}
            onAdd={() => openModal()}
            onEdit={openModal}
            onDelete={handleDelete}
            columns={getColumns('missions')}
          />
        )
      case 'impact_stats':
        return (
          <DataTable
            title="Impact Statistics Records"
            data={tableData}
            onAdd={() => openModal()}
            onEdit={openModal}
            onDelete={handleDelete}
            columns={getColumns('impact_stats')}
          />
        )
      case 'heritage_places':
        return (
          <DataTable
            title="Heritage Places"
            data={tableData}
            onAdd={() => openModal()}
            onEdit={openModal}
            onDelete={handleDelete}
            columns={getColumns('heritage_places')}
          />
        )
      case 'gallery_items':
        return (
          <DataTable
            title="Gallery Items (Before/After)"
            data={tableData}
            onAdd={() => openModal()}
            onEdit={openModal}
            onDelete={handleDelete}
            columns={getColumns('gallery_items')}
          />
        )
      case 'newsletters':
        return (
          <DataTable
            title="Newsletter Subscribers"
            data={tableData}
            onAdd={() => openModal()}
            onEdit={openModal}
            onDelete={handleDelete}
            columns={getColumns('newsletters')}
          />
        )
      case 'leaderboard':
        return (
          <DataTable
            title="Leaderboard & Gamification"
            data={tableData}
            onAdd={() => openModal()}
            onEdit={openModal}
            onDelete={handleDelete}
            columns={getColumns('leaderboard')}
          />
        )
      default:
        return null
    }
  }

  const getColumns = (tab) => {
    switch (tab) {
      case 'volunteers':
        return [
          { header: 'Name', accessor: 'full_name' },
          { header: 'Email', accessor: 'email', type: 'email' },
          { header: 'Mobile', accessor: 'mobile_number', type: 'tel' },
          { header: 'Skills', accessor: 'skills', optional: true },
          { header: 'Availability', accessor: 'availability', type: 'select', options: ['weekends', 'weekdays', 'anytime'], optional: true },
          { header: 'Status', accessor: 'status', type: 'select', options: ['pending', 'approved', 'active', 'inactive'], render: (row) => <StatusBadge status={row.status} /> },
          { header: 'Rank', accessor: 'rank', optional: true },
          { header: 'Total Hours', accessor: 'total_hours', type: 'number', optional: true },
          { header: 'Total Missions', accessor: 'total_missions', type: 'number', optional: true },
          { header: 'Date', accessor: 'created_at', render: (row) => new Date(row.created_at).toLocaleDateString() }
        ]
      case 'cleaning_slots':
        return [
          { header: 'Name', accessor: 'full_name' },
          { header: 'Email', accessor: 'email', type: 'email' },
          { header: 'Mobile', accessor: 'mobile_number', type: 'tel' },
          { header: 'Location', accessor: 'location_name', type: 'select', options: ['Fateh Sagar Lake', 'Pichola Lake', 'Gangaur Ghat', 'City Palace', 'Sajjangarh Monsoon Palace', 'Local Communities'] },
          { header: 'Slot', accessor: 'slot_timing', type: 'select', options: ['4 AM – 6 AM', '6 AM – 8 AM', '8 AM – 10 AM', '10 AM – 12 PM', '12 PM – 2 PM', '2 PM – 4 PM', '4 PM – 6 PM', '6 PM – 8 PM', '8 PM – 10 PM'] },
          { header: 'Status', accessor: 'status', type: 'select', options: ['pending', 'confirmed', 'completed', 'cancelled'], render: (row) => <StatusBadge status={row.status} /> },
          { header: 'Date', accessor: 'created_at', render: (row) => new Date(row.created_at).toLocaleDateString() }
        ]
      case 'missions':
        return [
          { header: 'Title', accessor: 'title' },
          { header: 'Description', accessor: 'description', type: 'textarea', optional: true },
          { header: 'Location', accessor: 'location', optional: true },
          { header: 'Category', accessor: 'category', type: 'select', options: ['cleanup', 'heritage', 'environment', 'community', 'youth'], optional: true },
          { header: 'Status', accessor: 'status', type: 'select', options: ['upcoming', 'ongoing', 'completed'], render: (row) => <StatusBadge status={row.status} /> },
          { header: 'Participants', accessor: 'total_participants', type: 'number', optional: true }
        ]
      case 'impact_stats':
        return [
          { header: 'Volunteers', accessor: 'total_volunteers', type: 'number' },
          { header: 'Missions', accessor: 'active_missions', type: 'number' },
          { header: 'Waste (kg)', accessor: 'waste_removed_kg', type: 'number' },
          { header: 'Lakes', accessor: 'lakes_cleaned', type: 'number' },
          { header: 'Hours', accessor: 'hours_contributed', type: 'number' },
          { header: 'Community Members', accessor: 'community_members', type: 'number', optional: true },
          { header: 'Updated', accessor: 'updated_at', render: (row) => new Date(row.updated_at).toLocaleDateString() }
        ]
      case 'heritage_places':
        return [
          { header: 'Name', accessor: 'name' },
          { header: 'Description', accessor: 'description', type: 'textarea' },
          { header: 'Icon (Emoji)', accessor: 'icon', optional: true },
          { header: 'Image URL', accessor: 'image_url', optional: true },
          { header: 'Volunteers', accessor: 'volunteers_active', type: 'number', optional: true },
          { header: 'Day', accessor: 'timing_day', type: 'select', options: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], optional: true },
          { header: 'Start Time', accessor: 'timing_start', type: 'time', optional: true },
          { header: 'End Time', accessor: 'timing_end', type: 'time', optional: true },
        ]
      case 'gallery_items':
        return [
          { header: 'Title', accessor: 'title' },
          { header: 'Description', accessor: 'description', type: 'textarea', optional: true },
          { header: 'Before URL', accessor: 'before_image_url' },
          { header: 'After URL', accessor: 'after_image_url' },
          { header: 'Tag', accessor: 'tag', type: 'select', options: ['Cleanup', 'Heritage', 'Community', 'Photography', 'Youth', 'Environment', 'Events'] },
        ]
      case 'newsletters':
        return [
          { header: 'Email', accessor: 'email', type: 'email' },
          { header: 'Subscribed At', accessor: 'subscribed_at', render: (row) => new Date(row.subscribed_at).toLocaleDateString() }
        ]
      case 'leaderboard':
        return [
          { header: 'Name', accessor: 'name' },
          { header: 'Hours Contributed', accessor: 'hours', type: 'number' },
          { header: 'Missions Completed', accessor: 'missions', type: 'number' },
          { header: 'Avatar (Emoji)', accessor: 'avatar', optional: true },
          { header: 'Date Added', accessor: 'created_at', render: (row) => new Date(row.created_at).toLocaleDateString() }
        ]
      default:
        return []
    }
  }

return (
    <div className="min-h-screen bg-dark-bg flex">
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className="flex-1 md:ml-64 p-4 sm:p-6 md:p-8 w-full">
          <header className="mb-6 md:mb-8 flex items-center justify-between">
            <div>
              <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="md:hidden text-gray-300 hover:text-white mb-4 transition">
                <FiMenu size={24} />
              </button>
              <h2 className="text-2xl sm:text-3xl md:text-3xl font-bold font-grotesk text-white capitalize">
                {activeTab.replace('_', ' ')}
              </h2>
              <p className="text-xs sm:text-sm text-gray-400">Manage and oversee platform data.</p>
            </div>
          </header>
          {renderContent()}
       </div>

      <AdminModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        data={editingData}
        columns={getColumns(activeTab)}
        title={activeTab.replace('_', ' ')}
      />
    </div>
  )
}

const StatCard = ({ title, value }) => (
  <div className="glass-card p-6 border-l-4 border-accent-maroon bg-white/10">
    <h4 className="text-gray-300 text-sm font-medium uppercase tracking-wider">{title}</h4>
    <p className="text-3xl font-bold text-white mt-2">{value !== undefined ? value : '--'}</p>
  </div>
)

const StatusBadge = ({ status }) => {
  let colorClass = 'bg-gray-500/20 text-gray-300 border-gray-500/30'
  
  if (['active', 'completed', 'approved', 'confirmed'].includes(status?.toLowerCase())) {
    colorClass = 'bg-green-500/20 text-green-300 border-green-500/30'
  } else if (['pending', 'ongoing', 'upcoming'].includes(status?.toLowerCase())) {
    colorClass = 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
  } else if (['cancelled', 'inactive'].includes(status?.toLowerCase())) {
    colorClass = 'bg-red-500/20 text-red-300 border-red-500/30'
  }

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${colorClass} capitalize`}>
      {status || 'Unknown'}
    </span>
  )
}

export default AdminDashboard
